import { useContextMenuMESF } from "@dexteel/mesf-core";
import { Grid, Typography } from "@mui/material";
import {
  DefaultMenuItem,
  GetContextMenuItemsParams,
  GridApi,
  IRowNode,
  MenuItemDef,
} from "ag-grid-community";
import { AgGridReact } from "ag-grid-react";

import { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { CenteredLazyLoading } from "../../../../../controls/LazyLoading";
import { useGridDefinitions } from "../../../../../controls/ag-grid/components/GridDefinitions";
import { FooterButton } from "../../../../../controls/buttons/FooterButton";
import { CatalogSampleType } from "../models/CatalogSampleType";
import { useSampleTypesOptionsFunctions } from "../hooks/useSampleTypeOptionsFunctions";
import { useTableData } from "../hooks/useTableData";
import { SampleTypeModal } from "./SampleTypeModal";

type onModalHideProps = {
  entityId?: number;
  shouldUpdate?: boolean;
};

type Props = {
  sampleTypeId: string | undefined;
  sampleTypes: CatalogSampleType[] | null;
  isLoading: boolean;
};

export const TableSampleType = ({
  sampleTypeId,
  sampleTypes,
  isLoading,
}: Props) => {
  const navigate = useNavigate();

  const [showModal, setShowModal] = useState<"new" | "edit" | "delete" | "">(
    "",
  );
  const [sampleType, setSampleType] = useState<CatalogSampleType | null>(null);
  const [gridApi, setGridApi] = useState<GridApi | null>(null);

  const { showContextMenu, registerConfig } = useContextMenuMESF();

  const handleNewSampleType = () => {
    if (gridApi) gridApi.deselectAll();
    setShowModal("new");
    setSampleType(null);
    navigate(`/configuration/lab-sample-types/new`);
  };

  const { getMenuOptions } = useSampleTypesOptionsFunctions({
    setSampleType,
    setShowModal,
    handleNewSampleType,
  });
  const { rows, columnDefs } = useTableData({
    sampleTypes,
    setSampleType,
    setShowModal,
    showContextMenu,
  });
  const { columnSimpleDefaults } = useGridDefinitions({
    OnEdit: (data: CatalogSampleType) => {
      setShowModal("edit");
      setSampleType(data);
    },
  });

  const onRowDoubleClicked = (event: any) => {
    const cell = gridApi?.getFocusedCell();
    if (cell?.column.getColId() !== "-" && event.node.isSelected()) {
      setSampleType(event.data as CatalogSampleType);
      setShowModal("edit");
      navigate(
        `/configuration/lab-sample-types/${
          (event.data as CatalogSampleType).SampleTypeId
        }`,
      );
    }
  };

  const onCellClicked = (event: any) => {
    if (event.column.colId === "-")
      navigate(
        `/configuration/lab-sample-types/${
          (event.data as CatalogSampleType).SampleTypeId
        }`,
      );
  };

  const getContextMenuItems = useCallback(
    (params: GetContextMenuItemsParams): (DefaultMenuItem | MenuItemDef)[] => {
      const data = params.node?.data;
      const nativeEvent = (
        params as GetContextMenuItemsParams & { event?: MouseEvent }
      ).event;

      if (data && nativeEvent) {
        showContextMenu(
          nativeEvent as unknown as React.MouseEvent,
          data,
          "sampleTypeContext",
        );
        params.api.deselectAll();
        params.node?.setSelected(true);
      }
      return [];
    },
    [rows],
  );

  const onModalHide = ({ entityId, shouldUpdate }: onModalHideProps) => {
    if (entityId && shouldUpdate) {
      setTimeout(() => {
        const rowNode = gridApi?.getRowNode(String(entityId));
        if (rowNode) {
          gridApi?.ensureIndexVisible(rowNode.rowIndex ?? 0, "middle");
          rowNode?.setSelected(true);
        }
      }, 0);
    }
    setShowModal("");
    setSampleType(null);
    navigate(`/configuration/lab-sample-types/`);
  };

  useEffect(() => {
    if (sampleTypeId) {
      if (sampleTypeId === "new") {
        handleNewSampleType();
      } else {
        let partNode: IRowNode<any>;

        gridApi?.forEachNode((n) => {
          if (
            (n.data as CatalogSampleType).SampleTypeId === Number(sampleTypeId)
          ) {
            partNode = n;
            return true;
          }
        });

        if (partNode!) {
          gridApi?.ensureIndexVisible(partNode.rowIndex ?? 0, "middle");
          partNode.setSelected(true);
          setSampleType(partNode.data);
          setShowModal("edit");
        }
      }
    }
  }, [gridApi]);

  useEffect(() => {
    registerConfig({
      id: "sampleTypeContext",
      getOptions: getMenuOptions,
    });
  }, []);

  return (
    <>
      <Grid container justifyContent="center">
        <Grid item md={12} xs={12} style={{ padding: "0 15px" }}>
          <Typography variant="h5" style={{ fontWeight: 600 }}>
            Sample Types
          </Typography>
        </Grid>
        <Grid
          container
          justifyContent="center"
          alignItems="center"
          style={{ width: "100%", padding: "0 15px" }}
        >
          <div style={{ width: "100%", height: "70vh" }}>
            <AgGridReact<CatalogSampleType>
              loading={isLoading}
              rowData={rows || []}
              columnDefs={columnDefs}
              defaultColDef={columnSimpleDefaults}
              rowHeight={34}
              headerHeight={34}
              animateRows
              rowSelection="single"
              getContextMenuItems={getContextMenuItems}
              onRowDoubleClicked={onRowDoubleClicked}
              loadingOverlayComponent={CenteredLazyLoading}
              onGridReady={(params) => setGridApi(params.api)}
              getRowId={(params) => String(params.data.SampleTypeId)}
              onCellClicked={onCellClicked}
            />
          </div>
          <FooterButton label="SAMPLE TYPE" onClick={handleNewSampleType} />
        </Grid>
      </Grid>
      <SampleTypeModal
        sampleType={sampleType}
        sampleTypes={sampleTypes}
        showModal={showModal}
        onHide={({ entityId, shouldUpdate }) => {
          onModalHide({ entityId, shouldUpdate });
        }}
      />
    </>
  );
};
